import React from 'react';
import { motion } from 'framer-motion';
import { subDays } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheckSquare, FiClock, FiTrendingUp, FiUsers } = FiIcons;

const StatsCards = ({ tasks, clients, teamMembers }) => {
  // Helper to calculate growth percentage based on Created Date
  // Compares last 30 days vs the 30 days before that
  const calculateGrowth = (items) => {
    if (!items || items.length === 0) return { val: 0, type: 'neutral' };

    const now = new Date();
    const thirtyDaysAgo = subDays(now, 30);
    const sixtyDaysAgo = subDays(now, 60);

    const thisMonthItems = items.filter(i => new Date(i.createdAt) >= thirtyDaysAgo);
    const lastMonthItems = items.filter(i => {
      const d = new Date(i.createdAt);
      return d >= sixtyDaysAgo && d < thirtyDaysAgo;
    });

    const currentCount = thisMonthItems.length;
    const prevCount = lastMonthItems.length;

    if (prevCount === 0) {
      return { val: currentCount > 0 ? 100 : 0, type: 'increase' };
    }

    const diff = ((currentCount - prevCount) / prevCount) * 100;
    return { 
      val: Math.abs(Math.round(diff)), 
      type: diff >= 0 ? 'increase' : 'decrease' 
    };
  };

  const taskGrowth = calculateGrowth(tasks);
  const clientGrowth = calculateGrowth(clients);
  const teamGrowth = calculateGrowth(teamMembers);
  
  // For Pending tasks, we might want to compare pending count snapshot (needs history) 
  // or just use creation rate of tasks that are currently pending.
  // For simplicity, we'll use creation rate of tasks.
  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const pendingGrowth = calculateGrowth(pendingTasks);

  const stats = [
    { 
      title: 'Total Tasks', 
      value: tasks.length, 
      change: `${taskGrowth.val}%`, 
      changeType: taskGrowth.type, 
      icon: FiCheckSquare, 
      color: 'blue' 
    },
    { 
      title: 'Active Clients', 
      value: clients.filter(c => c.status === 'active').length, 
      change: `${clientGrowth.val}%`, 
      changeType: clientGrowth.type, 
      icon: FiUsers, 
      color: 'green' 
    },
    { 
      title: 'Pending Tasks', 
      value: pendingTasks.length, 
      change: `${pendingGrowth.val}%`, 
      changeType: pendingGrowth.type, 
      icon: FiClock, 
      color: 'yellow' 
    },
    { 
      title: 'Team Members', 
      value: teamMembers.filter(t => t.status === 'active').length, 
      change: `${teamGrowth.val}%`, 
      changeType: teamGrowth.type, 
      icon: FiTrendingUp, 
      color: 'purple' 
    }
  ];

  const colorClasses = {
    blue: 'bg-blue-500 text-blue-600 bg-blue-50',
    green: 'bg-green-500 text-green-600 bg-green-50',
    yellow: 'bg-yellow-500 text-yellow-600 bg-yellow-50',
    purple: 'bg-purple-500 text-purple-600 bg-purple-50'
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {stats.map((stat, index) => (
        <motion.div
          key={stat.title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          whileHover={{ y: -2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{stat.title}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
              <div className="flex items-center mt-2">
                <span className={`text-sm font-medium ${
                  stat.changeType === 'increase' ? 'text-green-600' : 
                  stat.changeType === 'decrease' ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {stat.changeType === 'increase' ? '+' : stat.changeType === 'decrease' ? '-' : ''}{stat.change}
                </span>
                <span className="text-sm text-gray-500 ml-1">vs last 30 days</span>
              </div>
            </div>
            <div className={`p-3 rounded-lg ${colorClasses[stat.color].split(' ')[2]}`}>
              <SafeIcon icon={stat.icon} className={`w-6 h-6 ${colorClasses[stat.color].split(' ')[1]}`} />
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default StatsCards;